const UNSIGNED_INTEGER_PATTERN = /^\d+$/

// Kambi and Sports Search quote American odds as signed strings ("+120",
// "-110") and decimal odds as numbers; these helpers convert and format both.

/**
 * American odds with a '+' on positive prices ("+150", "-110"). `empty` is
 * returned when there is no price (null, blank or 0).
 */
export function formatAmericanOdds(value, { empty = '' } = {}) {
  if (value == null || value === '' || Number(value) === 0) return empty
  const american = Number(value)
  return american > 0 ? `+${american}` : String(american)
}

/** Decimal odds for American odds ("+150" → 2.5, -200 → 1.5); null when unreadable or 0. */
export function americanToDecimal(value) {
  const american = Number(String(value ?? '').replace('+', ''))
  if (!Number.isFinite(american) || american === 0) return null
  return american > 0 ? 1 + american / 100 : 1 + 100 / Math.abs(american)
}

/** Decimal odds to two places ("1.91"); empty when there is no price above evens. */
export function formatDecimalOdds(value) {
  const decimal = Number(value)
  return Number.isFinite(decimal) && decimal > 1 ? decimal.toFixed(2) : ''
}

/** The combined decimal price of several legs; null when there are none or any price is missing. */
export function combinedDecimalOdds(values) {
  if (!values?.length) return null
  let total = 1
  for (const value of values) {
    const decimal = Number(value)
    if (!Number.isFinite(decimal) || decimal <= 1) return null
    total *= decimal
  }
  return total
}

/**
 * A handicap or total line as text ("-3.5", "48.5"); empty when the market has
 * none. `signed` marks a positive handicap "+3.5"; a pick'em stays "0".
 */
export function formatLine(value, { signed = false } = {}) {
  if (value === null || value === undefined || value === '') return ''
  const line = Number(value)
  if (!Number.isFinite(line)) return String(value)
  if (line === 0) return '0'
  return signed && line > 0 ? `+${line}` : String(line)
}

/** A Kambi outcome ID typed or returned as text: a positive safe integer, or null. */
export function parseOutcomeId(value) {
  const normalized = String(value ?? '').trim()
  if (!UNSIGNED_INTEGER_PATTERN.test(normalized)) return null
  const id = Number(normalized)
  return Number.isSafeInteger(id) && id > 0 ? id : null
}
